import React, { useState } from "react";
import { GroupType } from "@src/types.generated";
import { Button } from "@src/lib/ui/Button";
import { Input } from "@src/lib/ui/Input";
import Text from "@src/lib/ui/Text";
import useIsFullAdmin from "@src/features/app/hooks/useIsFullAdmin";
import PlusIcon from "@src/lib/icons/18px/plus";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";

type ThinGroup = {
  id: number;
  name: string;
};

type Props<T extends ThinGroup> = {
  groups: T[];
  group?: T;
  setGroup: (group: T) => void;
  label?: string;
  groupType: GroupType;
  createGroup?: (name: string) => void;
  dataTestId?: string;
};

const GroupSelect = <T extends ThinGroup>({
  groups,
  group,
  setGroup,
  label,
  groupType,
  createGroup,
  dataTestId,
}: Props<T>): React.ReactElement => {
  const [open, setOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const isFullAdmin = useIsFullAdmin();
  const handleCreateGroup = () => {
    if (createGroup && newGroupName) {
      createGroup(newGroupName);
      setIsCreating(false);
      setNewGroupName("");
    }
  };
  const groupTypeLabel = groupType.toLowerCase();
  return (
    <div className="w-full min-w-0">
      {label && (
        <Text variant="compact-title" className="mb-2">
          {label}
        </Text>
      )}
      <Popover
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) {
            setIsCreating(false);
            setNewGroupName("");
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="flex w-full justify-between px-3"
            data-testid={dataTestId}
          >
            <span className={group ? "truncate" : "truncate text-gray-5"}>
              {group?.name || `Select ${groupTypeLabel}`}
            </span>
            <ChevronDownIcon className="ml-2 size-4 shrink-0 text-gray-5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          disablePortal
        >
          <Command>
            {!isCreating && (
              <CommandInput placeholder="Search..." className="h-9" />
            )}
            <CommandList>
              <CommandEmpty>No {groupTypeLabel} found</CommandEmpty>
              <CommandGroup>
                {groups.map((item) => (
                  <CommandItem
                    key={item.id}
                    value={`${item.name} ${item.id}`}
                    data-testid="select-item"
                    className="truncate"
                    onSelect={() => {
                      setGroup(item);
                      setOpen(false);
                    }}
                  >
                    {item.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
          {isFullAdmin && createGroup && !isCreating && (
            <div className="flex justify-center border-t border-border p-1 pt-2">
              <Button
                variant="ghost"
                className="w-full text-primary hover:bg-blue-1"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setIsCreating(true)}
              >
                <PlusIcon className="size-4" />
                Create {groupTypeLabel}
              </Button>
            </div>
          )}
          {isCreating && (
            <div className="p-2">
              <Input
                placeholder={`New ${groupTypeLabel} name`}
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="mb-2"
                autoFocus
              />
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreating(false)}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCreateGroup}>
                  Create
                </Button>
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default GroupSelect;
